import { UPLOAD_RESOURCE_LOCK_PREFIX } from '@/features/uploads/config/uploadCoordination';

export interface UploadResourceIdentity {
  uploaderId: string;
  contentBlake3: string;
}

interface PendingFallbackLock {
  tail: Promise<void>;
  release: () => void;
}

const fallbackLocks = new Map<string, PendingFallbackLock>();

/**
 * A content-addressed lock name shared by every Vision Lab tab on the same origin. The backend's
 * submission uniqueness boundary is uploader + BLAKE3, so the browser uses the same identity rather
 * than a filename (which is neither stable nor unique).
 */
function uploadResourceLockName(identity: UploadResourceIdentity): string {
  return `${UPLOAD_RESOURCE_LOCK_PREFIX}:${identity.uploaderId}:${identity.contentBlake3}`;
}

function abortError(): DOMException {
  return new DOMException('The upload lock request was aborted', 'AbortError');
}

function waitUntilAvailable(wait: Promise<void>, signal?: AbortSignal): Promise<void> {
  if (!signal) return wait;
  if (signal.aborted) return Promise.reject(abortError());
  return new Promise((resolve, reject) => {
    const onAbort = () => reject(abortError());
    signal.addEventListener('abort', onAbort, { once: true });
    void wait.then(resolve, reject).finally(() => signal.removeEventListener('abort', onAbort));
  });
}

/** Same-document fallback for test runners and old browsers without Web Locks. */
async function withFallbackLock<T>(
  name: string,
  signal: AbortSignal | undefined,
  run: () => Promise<T>,
): Promise<T> {
  const previous = fallbackLocks.get(name)?.tail ?? Promise.resolve();
  let release = () => {};
  const acquired = new Promise<void>((resolve) => {
    release = resolve;
  });
  const tail = previous.catch(() => {}).then(() => acquired);
  const pending = { tail, release };
  fallbackLocks.set(name, pending);

  try {
    await waitUntilAvailable(previous, signal);
    if (signal?.aborted) throw abortError();
    return await run();
  } finally {
    release();
    if (fallbackLocks.get(name) === pending) fallbackLocks.delete(name);
  }
}

/**
 * Run all mutable work for one local-upload resource under a browser-origin-wide exclusive lock.
 * Web Locks are released automatically if the owner tab closes, allowing another tab to reconcile
 * the backend offset and take over safely. Callers must re-read backend session state inside `run`;
 * that is what turns a queued second tab into a no-op after the first tab completed.
 */
export async function withUploadResourceLock<T>(
  identity: UploadResourceIdentity,
  signal: AbortSignal | undefined,
  run: () => Promise<T>,
): Promise<T> {
  const name = uploadResourceLockName(identity);
  const lockManager = globalThis.navigator?.locks;
  if (!lockManager) return withFallbackLock(name, signal, run);

  return lockManager.request(name, { mode: 'exclusive', signal }, async () => run());
}
